import { BlockWithAlignableContents } from "@lexical/react/LexicalBlockWithAlignableContents";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Box, CircularProgress, LinearProgress, Stack, Typography } from "@mui/material";
import type { ElementFormatType, NodeKey } from "lexical";
import { $getNodeByKey, COMMAND_PRIORITY_EDITOR } from "lexical";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useApiLoader } from "../../server/api/hooks/use-api-loader";
import { useToken } from "../../server/auth/use-token";
import { useDialog } from "../../utils/hooks/use-dialog";
import type { UUID } from "../../utils/uuid";
import { useClaims } from "../generic/hooks/use-claims";
import { ImageTools } from "../message/editor/components/image-tools";
import { LinkForm } from "../message/editor/components/link-form/link-form";
import type { Link } from "../message/editor/components/link-form/types";
import { useLexicalContext } from "../message/editor/lexical-context";
import type { DesignHuddleCustomisations } from "../message/editor/nodes/design-huddle-node";
import { $isDesignHuddleNode } from "../message/editor/nodes/design-huddle-node";
import { VideoTitle } from "../message/editor/nodes/video-title";
import { DESIGN_HUDDLE_EDIT_COMMAND } from "../message/editor/types/commands";
import { DesignHuddleModal } from "./design-huddle-modal";
import { FailedDesignHuddlePreview } from "./failed-design-huddle-preview";
import { usePublishDesignHuddleTemplateWithTags } from "./use-publish-template-with-tags";
import { copyImage, getProgressText, imageURL } from "./utils";

export const DesignHuddlePreview: FC<{
  project_id?: string;
  account_id: string;
  page_number?: number;
  page_id?: string;
  proportionOfMaxWidth: number;
  aspect: number;
  format?: ElementFormatType;
  nodeKey: NodeKey;
  must_be_cloned?: boolean;
  href?: string;
  preview_url?: string;
  content_type: "video" | "image";
  asset_id?: UUID;
  title?: string;
}> = ({
  account_id,
  project_id,
  page_number,
  page_id,
  nodeKey,
  proportionOfMaxWidth,
  aspect,
  format,
  must_be_cloned,
  href,
  preview_url,
  content_type,
  asset_id,
  title,
}) => {
  const [editor] = useLexicalComposerContext();
  const { filterTags, channelName: channel } = useLexicalContext();
  const { handleClose: closeLinkDialog, open: linkDialogOpen } = useDialog();
  const publishTemplate = usePublishDesignHuddleTemplateWithTags();

  const designHuddleDialog = useDialog();

  const [disabled, setDisabled] = useState(!editor.isEditable());
  const [noCache, setNoCache] = useState(new Date().getTime());
  const user = useClaims();
  const getToken = useToken();

  const [progress, setProgress] = useState<number | null>(null);

  const { error: previewUrlError } = useApiLoader(async () => {
    if (!project_id || !preview_url) return;

    const image = await copyImage(getToken, preview_url, project_id);
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isDesignHuddleNode(node)) {
        node.setPreviewUrl(undefined);
      }
    });

    return image;
  }, [editor, getToken, nodeKey, project_id, preview_url]);

  useEffect(() => {
    if (previewUrlError) {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if ($isDesignHuddleNode(node)) {
          console.log("Error loading image");
          node.setError(true);
        }
      });
      toast.error("Failed to load image.");
    }
  }, [editor, nodeKey, previewUrlError]);

  const templatePublishRequired = useCallback(() => {
    if (!project_id) return;
    return editor.read(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isDesignHuddleNode(node) && node.__checkTags) {
        setProgress(10);
        return true;
      }
      return false;
    });
  }, [editor, nodeKey, project_id]);

  useEffect(() => {
    if (!project_id) return;

    (async () => {
      if (templatePublishRequired()) {
        const customisations = await publishTemplate(project_id, setProgress);
        editor.update(() => {
          const node = $getNodeByKey(nodeKey);
          customisations && $isDesignHuddleNode(node) && node.templateChangeUpdate(customisations);
        });
        setProgress(null);
      }
    })().catch(console.error);
  }, [project_id, editor, nodeKey, publishTemplate, templatePublishRequired]);

  const onProjectCloned = useCallback(
    (projectId: string): void => {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if ($isDesignHuddleNode(node)) {
          node.setProjectId(projectId);
          node.setMustBeCloned(undefined);
        }
      });
    },
    [editor, nodeKey],
  );

  const handleUpdate = useCallback(
    ({
      page: page_num,
      clonedProjectId,
      customisations,
      publicRenderUrl,
    }: {
      page?: number;
      clonedProjectId?: string;
      customisations: DesignHuddleCustomisations | undefined;
      publicRenderUrl?: string;
    }) => {
      setNoCache(new Date().getTime());
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if ($isDesignHuddleNode(node)) {
          node.setPageNumber(page_num);
          node.setPageId(undefined);
          node.templateChangeUpdate(customisations);
          if (node.__contentType === "video") {
            node.setHref(publicRenderUrl);
          }
        }
      });
      if (clonedProjectId) onProjectCloned(clonedProjectId);
    },
    [editor, onProjectCloned, nodeKey],
  );

  useEffect(() => editor.registerEditableListener((x) => setDisabled(!x)), [editor]);

  // Listen for commands and check it's for us as these now come out of band
  useEffect(
    () =>
      editor.registerCommand(
        DESIGN_HUDDLE_EDIT_COMMAND,
        ({ nodeKey: editAttemptNodeKey }) => {
          if (nodeKey !== editAttemptNodeKey) return false;
          if (!previewUrlError) {
            designHuddleDialog.handleOpen();
          }
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    [editor, nodeKey, designHuddleDialog, previewUrlError],
  );

  const handleLinkUpdate = useCallback(
    ({ url }: Link) => {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if ($isDesignHuddleNode(node)) node.setHref(url);

        closeLinkDialog();
      });
    },
    [editor, nodeKey, closeLinkDialog],
  );

  const handleLinkRemove = useCallback(() => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isDesignHuddleNode(node)) node.setHref();

      closeLinkDialog();
    });
  }, [editor, nodeKey, closeLinkDialog]);

  const handleSetTitle = useCallback(
    (newTitle: string | undefined) => {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if ($isDesignHuddleNode(node)) {
          node.setTitle(newTitle);
        }
      });
    },
    [nodeKey, editor],
  );

  if (!user) return null;

  if (previewUrlError) return <FailedDesignHuddlePreview nodeKey={nodeKey} />;

  return (
    <>
      <BlockWithAlignableContents
        // These classes are defined in ../theme/theme.css
        className={{ base: format ? `flex-justify-${format}` : "", focus: "" }}
        format={format}
        nodeKey={nodeKey}
      >
        {content_type === "video" && (
          <VideoTitle
            nodeKey={nodeKey}
            title={title}
            asset_id={asset_id}
            width={`${proportionOfMaxWidth * 100}%`}
            setNodeTitle={handleSetTitle}
          />
        )}
        <ImageTools
          proportionOfMaxWidth={proportionOfMaxWidth ?? 1}
          aspect={aspect ?? 1}
          disabled={disabled || progress !== null}
          title=""
          maxWidth={channel === "slack" ? 600 : undefined}
          maxHeight={channel === "slack" ? 600 : undefined}
          nodeKey={nodeKey}
          resizable={true}
        >
          {project_id && !preview_url ? (
            <Box position="relative" width="100%" height="100%">
              <img
                src={`${imageURL(account_id, project_id)}&noCache=${noCache}`}
                alt=""
                role="img"
                style={{
                  width: "100%",
                  height: "100%",
                }}
                data-testid="design-huddle-preview"
              />
              {progress && (
                <Box
                  position="absolute"
                  bottom={0}
                  left={0}
                  right={0}
                  height="20%"
                  bgcolor="rgba(0, 0, 0, 0.7)"
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="center"
                  gap={2}
                  px={3}
                  sx={{
                    minHeight: "20px",
                    maxHeight: "40px",
                    background:
                      "linear-gradient(to right, rgba(242, 242, 242, 0.9), rgba(242, 242, 242, 0.4))",
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)", // For Safari support
                  }}
                >
                  <Typography variant="body2" sx={{ paddingBottom: "0px !important" }}>
                    {getProgressText(progress)}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      flex: 1,
                      height: 6,
                    }}
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              width="100%"
              sx={{
                backgroundColor: "action.hover",
              }}
            >
              <Stack direction="column" spacing={2} justifyContent="center" alignItems="center">
                <CircularProgress />
                <Typography variant="subtitle1">Loading Canvas...</Typography>
              </Stack>
            </Box>
          )}
        </ImageTools>
      </BlockWithAlignableContents>

      {project_id && designHuddleDialog.open && (
        <DesignHuddleModal
          open={designHuddleDialog.open}
          setOpen={designHuddleDialog.setOpen}
          project_id={project_id}
          account_id={account_id}
          page_number={page_number}
          page_id={page_id}
          onSave={handleUpdate}
          must_be_cloned={must_be_cloned}
          onProgress={setProgress}
          content_type={content_type}
        />
      )}
      {linkDialogOpen && (
        <LinkForm
          onRemove={handleLinkRemove}
          onCancel={closeLinkDialog}
          onConfirm={handleLinkUpdate}
          disabled={disabled}
          filterTags={filterTags}
          data={
            href
              ? { mode: "edit", url: href, type: "image" }
              : { mode: "add", url: undefined, type: "image" }
          }
        />
      )}
    </>
  );
};
